import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { hashPassword } from '../../utils/auth';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiLock, FiSave, FiEye, FiEyeOff, FiCheckCircle, FiAlertCircle, FiSettings, FiShield, FiHelpCircle, FiPlayCircle } = FiIcons;

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    password: '',
    confirmPassword: '',
    feedback_active: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        firstname: user.firstname || '',
        lastname: user.lastname || '',
        feedback_active: user.feedback_active === 1 || user.feedback_active === '1' || user.feedback_active === true
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleResetTour = async () => {
    try {
      setResetLoading(true);
      setMessage({ type: '', text: '' });
      
      // Clear saved step index to ensure fresh start
      localStorage.removeItem('joyride_step_index');
      
      const response = await apiService.updateUser(user.id, { onboarding_tour: 0 });
      
      if (response) {
        // Update local user context to trigger the tour in OnboardingTour component
        updateUser({
          ...user,
          onboarding_tour: 0
        });
        setMessage({ type: 'success', text: 'Onboarding Tour wurde zurückgesetzt und startet nun erneut.' });
      }
    } catch (error) {
      console.error('Error resetting tour:', error);
      setMessage({ type: 'error', text: 'Fehler beim Zurücksetzen der Tour.' });
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (formData.password && formData.password !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Die Passwörter stimmen nicht überein.' });
      setLoading(false);
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setMessage({ type: 'error', text: 'Das Passwort muss mindestens 6 Zeichen lang sein.' });
      setLoading(false);
      return;
    }

    try {
      const updateData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        feedback_active: formData.feedback_active ? 1 : 0
      };

      if (formData.password) {
        updateData.password = hashPassword(formData.password);
      }

      const response = await apiService.updateUser(user.id, updateData);

      if (response && (response.status === 'success' || response.id)) {
        updateUser({
          ...user,
          firstname: formData.firstname,
          lastname: formData.lastname,
          feedback_active: formData.feedback_active ? 1 : 0
        });

        setMessage({ type: 'success', text: 'Profil erfolgreich aktualisiert.' });
        
        setFormData(prev => ({
          ...prev,
          password: '',
          confirmPassword: ''
        }));
      } else {
        throw new Error('Update fehlgeschlagen');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Fehler beim Aktualisieren des Profils.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-brand-dark">Mein Profil</h1>
        <p className="mt-2 text-gray-600">Verwalten Sie Ihre persönlichen Daten und Sicherheitseinstellungen.</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100 bg-gray-50">
          <h2 className="text-lg font-medium text-brand-dark flex items-center">
            <SafeIcon icon={FiUser} className="mr-2 text-brand-secondary" />
            Persönliche Informationen
          </h2>
        </div>

        <div className="p-6">
          {message.text && (
            <div className={`mb-6 p-4 rounded-md flex items-start ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              <SafeIcon icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle} className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <span>{message.text}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstname" className="block text-sm font-medium text-gray-700 mb-1">
                  Vorname
                </label>
                <input
                  type="text"
                  id="firstname"
                  name="firstname"
                  value={formData.firstname}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                />
              </div>

              <div>
                <label htmlFor="lastname" className="block text-sm font-medium text-gray-700 mb-1">
                  Nachname
                </label>
                <input
                  type="text"
                  id="lastname"
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-Mail
                </label>
                <input
                  type="email"
                  value={user?.mail || user?.email || ''}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">Die E-Mail-Adresse kann nicht geändert werden.</p>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6 mt-6">
              <h3 className="text-lg font-medium text-brand-dark mb-4 flex items-center">
                <SafeIcon icon={FiSettings} className="mr-2 text-brand-secondary" />
                Einstellungen & Datenschutz
              </h3>
              
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="feedback_active"
                      name="feedback_active"
                      type="checkbox"
                      checked={formData.feedback_active}
                      onChange={handleChange}
                      className="focus:ring-brand-primary h-4 w-4 text-brand-primary border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="feedback_active" className="font-medium text-gray-700">
                      Teilnahme am Team-Feedback
                    </label>
                    <p className="text-gray-500 mt-1">
                      Ich stimme zu, dass ich Feedback von anderen Teammitgliedern erhalten und anderen Teammitgliedern Feedback geben kann. 
                      Die Auswertung erfolgt anonymisiert und aggregiert. Ich kann diese Einwilligung jederzeit widerrufen.
                    </p>
                    <div className="mt-2 flex items-center text-xs text-blue-700">
                      <SafeIcon icon={FiShield} className="w-3 h-3 mr-1" />
                      Datenschutzhinweis: Ihre Daten werden vertraulich behandelt.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6 mt-6">
              <h3 className="text-lg font-medium text-brand-dark mb-4 flex items-center">
                <SafeIcon icon={FiHelpCircle} className="mr-2 text-brand-secondary" />
                Hilfe & Einführung
              </h3>
              
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h4 className="font-medium text-gray-700">Onboarding Tour</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Starten Sie die Einführungstour erneut, um alle Funktionen der App kennenzulernen.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleResetTour}
                  disabled={resetLoading}
                  className="w-full sm:w-auto flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50"
                >
                  <SafeIcon icon={FiPlayCircle} className="mr-2 h-4 w-4" />
                  Tour neu starten
                </button>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-6 mt-6">
              <h3 className="text-lg font-medium text-brand-dark mb-4 flex items-center">
                <SafeIcon icon={FiLock} className="mr-2 text-brand-secondary" />
                Passwort ändern
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Lassen Sie diese Felder leer, wenn Sie Ihr Passwort nicht ändern möchten.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Neues Passwort
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Mindestens 6 Zeichen"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    >
                      <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Passwort bestätigen
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={loading}
                className={`flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-secondary hover:bg-brand-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                <SafeIcon icon={FiSave} className="mr-2 -ml-1 h-5 w-5" />
                {loading ? 'Speichere...' : 'Änderungen speichern'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;